import React, { useState } from "react";
import { MdClose } from "@react-icons/all-files/md/MdClose";
import { MdMic } from "@react-icons/all-files/md/MdMic";
import {
  MicrophonePermissionModalOverlay,
  MicrophonePermissionModalCard,
  MicrophonePermissionModalHeader,
  MicrophonePermissionModalCloseButton,
  MicrophonePermissionModalTitle,
  MicrophonePermissionModalSubtitle,
  MicrophonePermissionIcon,
  MicrophonePermissionInstructions,
  MicrophonePermissionInstructionItem,
  MicrophonePermissionModalButtons,
  MicrophonePermissionModalButton,
  ErrorMessage,
} from "./styled";

interface MicrophonePermissionModalProps {
  isOpen: boolean;
  onClose: () => void;
  onRequestPermission: () => void;
  translations: any;
}

const MicrophonePermissionModal: React.FC<MicrophonePermissionModalProps> = ({
  isOpen,
  onClose,
  onRequestPermission,
  translations,
}) => {
  const [hasError, setHasError] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");

  if (!isOpen) return null;

  if (!translations?.microphonePermission) {
    return null;
  }

  const permissionModal = translations.microphonePermission;

  const handleRequestPermission = async () => {
    setHasError(false);
    setErrorMessage("");
    try {
      await onRequestPermission();
    } catch (error: any) {
      setHasError(true);
      const isBlocked =
        error?.name === "NotAllowedError" ||
        error?.message?.includes("Permission denied");
      setErrorMessage(
        isBlocked
          ? permissionModal.errorBlocked
          : error?.message || permissionModal.errorBlocked
      );
    }
  };

  return (
    <MicrophonePermissionModalOverlay>
      <MicrophonePermissionModalCard>
        <MicrophonePermissionModalHeader>
          <MicrophonePermissionModalCloseButton onClick={onClose}>
            <MdClose size={24} />
          </MicrophonePermissionModalCloseButton>
          <MicrophonePermissionIcon>
            <MdMic size={48} />
          </MicrophonePermissionIcon>
          <MicrophonePermissionModalTitle>
            {permissionModal.title}
          </MicrophonePermissionModalTitle>
          <MicrophonePermissionModalSubtitle>
            {permissionModal.subtitle}
          </MicrophonePermissionModalSubtitle>
        </MicrophonePermissionModalHeader>

        {hasError ? (
          <>
            <ErrorMessage>{errorMessage}</ErrorMessage>
            <MicrophonePermissionInstructions>
              <MicrophonePermissionInstructionItem>
                {permissionModal.blockedInstructions.step1}
              </MicrophonePermissionInstructionItem>
              <MicrophonePermissionInstructionItem>
                {permissionModal.blockedInstructions.step2}
              </MicrophonePermissionInstructionItem>
              <MicrophonePermissionInstructionItem>
                {permissionModal.blockedInstructions.step3}
              </MicrophonePermissionInstructionItem>
              <MicrophonePermissionInstructionItem>
                {permissionModal.blockedInstructions.step4}
              </MicrophonePermissionInstructionItem>
              <MicrophonePermissionInstructionItem>
                {permissionModal.blockedInstructions.step5}
              </MicrophonePermissionInstructionItem>
              <MicrophonePermissionInstructionItem>
                {permissionModal.blockedInstructions.step6}
              </MicrophonePermissionInstructionItem>
            </MicrophonePermissionInstructions>
          </>
        ) : (
          <MicrophonePermissionInstructions>
            <MicrophonePermissionInstructionItem>
              {permissionModal.instructions.step1}
            </MicrophonePermissionInstructionItem>
            <MicrophonePermissionInstructionItem>
              {permissionModal.instructions.step2}
            </MicrophonePermissionInstructionItem>
            <MicrophonePermissionInstructionItem>
              {permissionModal.instructions.step3}
            </MicrophonePermissionInstructionItem>
          </MicrophonePermissionInstructions>
        )}

        <MicrophonePermissionModalButtons>
          {hasError ? (
            <>
              <MicrophonePermissionModalButton
                variant="secondary"
                onClick={() => {
                  window.location.reload();
                }}
              >
                {permissionModal.buttons.reloadPage}
              </MicrophonePermissionModalButton>
              <MicrophonePermissionModalButton
                variant="primary"
                onClick={() => {
                  setHasError(false);
                  setErrorMessage("");
                  handleRequestPermission();
                }}
              >
                {permissionModal.buttons.tryAgain}
              </MicrophonePermissionModalButton>
            </>
          ) : (
            <MicrophonePermissionModalButton
              variant="primary"
              onClick={handleRequestPermission}
            >
              {permissionModal.buttons.requestPermission}
            </MicrophonePermissionModalButton>
          )}
        </MicrophonePermissionModalButtons>
      </MicrophonePermissionModalCard>
    </MicrophonePermissionModalOverlay>
  );
};

export default MicrophonePermissionModal;
